// Phase SIKA-5 Layout — Panel 4: Protection Vault.
//
// Source: video frames 4/9 — header + SECURED pill, huge gold stat,
// FUNDING BREAKDOWN 3-segment bar (BANKED | ACTIVE | REMAINING) and
// WEEKLY PROTECTION HISTORY table.
//
// Doctrine:
//   N01 — Simple Mode reads banked_amount from sika_goals only. No
//         dependency on rollover_chains.
//   Chain Mode reads cumulative_protected_amount from
//   chain_protected_amounts (via sika_chain_protection_view) — same
//   underlying data ROLLOVER writes, SIKA-voiced presentation.

import { Eyebrow, Pill } from "../super-acca";
import type { SikaGoalRow, SikaMilestoneRow } from "../../sika/useSikaGoal.ts";

export interface ChainProtectionWeekRow {
  week_number: number;
  amount_from_this_week: number;
  cumulative_protected_amount: number;
}

export interface ChainProtectionSummary {
  cumulative: number;
  weeks: ChainProtectionWeekRow[];
}

export interface ProtectionVaultPanelProps {
  goal: SikaGoalRow | null;
  milestones: SikaMilestoneRow[];
  pro?: boolean;
  chainProtection?: ChainProtectionSummary | null;
}

function ghc(amount: number): string {
  return `GH₵${amount.toLocaleString("en-GH", { maximumFractionDigits: 0 })}`;
}

function ghcK(amount: number): string {
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}k`;
  return amount.toFixed(0);
}

/** 3-segment funding breakdown bar. Inline SVG, existing tokens. */
function FundingBreakdownBar({ banked, active, remaining }: { banked: number; active: number; remaining: number }) {
  const total = Math.max(1, banked + active + remaining);
  const pct = (x: number): number => (x / total) * 100;
  return (
    <div data-testid="sika-funding-breakdown-bar" style={{ marginTop: 10 }}>
      <div
        style={{
          display: "flex",
          width: "100%",
          height: 12,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid var(--sa-hairline)",
        }}
      >
        <div
          style={{
            width: `${pct(banked).toFixed(1)}%`,
            background: "var(--sa-signal-green)",
          }}
          title={`Banked ${ghc(banked)}`}
        />
        <div
          style={{
            width: `${pct(active).toFixed(1)}%`,
            background: "var(--sa-accent-saffron)",
          }}
          title={`Active ${ghc(active)}`}
        />
        <div
          style={{
            width: `${pct(remaining).toFixed(1)}%`,
            background: "var(--sa-hairline)",
          }}
          title={`Remaining ${ghc(remaining)}`}
        />
      </div>
      <div
        style={{
          marginTop: 6,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.08em",
        }}
      >
        <span>BANKED {ghcK(banked)}</span>
        <span>ACTIVE {ghcK(active)}</span>
        <span>REMAINING {ghcK(remaining)}</span>
      </div>
    </div>
  );
}

export function ProtectionVaultPanel({
  goal,
  milestones,
  pro = false,
  chainProtection = null,
}: ProtectionVaultPanelProps) {
  if (!goal) {
    return (
      <div style={{ padding: 18 }} data-panel="protection-vault">
        <Eyebrow>Panel 4 · Protection Vault</Eyebrow>
        <div
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 13,
            color: "var(--sa-fg-muted)",
          }}
        >
          Once a goal is active, every protected amount lands here.
        </div>
      </div>
    );
  }

  // N01 — Simple Mode reads banked_amount from sika_goals (no chain dependency).
  // Chain Mode wires chain_protected_amounts via sika_chain_protection_view.
  const isChainMode = goal.mode === "chain" && goal.linked_chain_id !== null;
  const secured = isChainMode
    ? (chainProtection?.cumulative ?? 0)
    : Number(goal.banked_amount ?? 0);
  const active = Math.max(0, goal.current_amount - secured);
  const remaining = Math.max(0, goal.target_amount - goal.current_amount);
  const securedPct = goal.target_amount > 0
    ? Math.round((secured / goal.target_amount) * 100)
    : 0;

  // Weekly protection history rows — Chain Mode reads chain protection
  // weeks; Simple Mode falls back to milestones (honest empty state if none).
  const historyRows: { label: string; amount: number; note: string }[] = isChainMode
    ? (chainProtection?.weeks ?? []).map((w) => ({
        label: `W${String(w.week_number).padStart(2, "0")}`,
        amount: w.amount_from_this_week,
        note: w.amount_from_this_week > 0
          ? `Protection banked`
          : `Chain reset — protection unchanged`,
      }))
    : [...milestones]
        .sort((a, b) => new Date(a.triggered_at).getTime() - new Date(b.triggered_at).getTime())
        .slice(0, 7)
        .map((m, i) => ({
          label: `W${String(i + 1).padStart(2, "0")}`,
          amount: Number(m.milestone_amount_ghc ?? 0),
          note: m.milestone_type.replace(/_/g, " "),
        }));
  const totalWeeks = historyRows.length;
  const protectedWeeks = historyRows.filter((r) => r.amount > 0).length;
  const rate = totalWeeks > 0 ? Math.round((protectedWeeks / totalWeeks) * 100) : 0;

  return (
    <div
      style={{ padding: 22 }}
      data-panel="protection-vault"
      data-mode={isChainMode ? "chain" : "simple"}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <Eyebrow>— Panel 04{isChainMode ? " · Chain Mode" : ""}</Eyebrow>
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--sa-font-stat)",
              fontSize: 22,
              letterSpacing: "0.02em",
              color: "var(--sa-fg-primary)",
            }}
          >
            Protection Vault
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.06em",
            }}
          >
            {isChainMode
              ? `From ROLLOVER chain ${goal.linked_chain_id ? `${goal.linked_chain_id.slice(0, 8)}…` : ""}`
              : "Reads from ROLLOVER cumulative protection"}
          </div>
        </div>
        <Pill
          borderColor="var(--sa-signal-green)"
          color="var(--sa-signal-green)"
          background="rgba(80,200,120,0.08)"
        >
          SECURED
        </Pill>
      </div>

      <div style={{ marginTop: 18 }}>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--sa-accent-saffron)",
          }}
        >
          {ghc(secured)} secured · {String(securedPct)}% of target
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: "var(--sa-font-stat)",
            fontSize: 44,
            color: "var(--sa-accent-saffron)",
            letterSpacing: "0.01em",
            lineHeight: 1,
          }}
          data-testid="sika-vault-secured-amount"
        >
          {ghc(secured)}
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.5,
          }}
        >
          Even if this week's selection loses — {ghc(secured)} is guaranteed toward {goal.title}.
        </div>
      </div>

      {/* FUNDING BREAKDOWN bar */}
      <div style={{ marginTop: 18 }}>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--sa-fg-muted)",
          }}
        >
          Funding breakdown
        </div>
        <FundingBreakdownBar banked={secured} active={active} remaining={remaining} />
      </div>

      {/* WEEKLY PROTECTION HISTORY */}
      <div style={{ marginTop: 18 }} data-testid="sika-weekly-protection-history">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 8,
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "var(--sa-fg-muted)",
            }}
          >
            Weekly protection history
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--sa-fg-muted)",
            }}
          >
            RATE {String(rate)}%
          </div>
        </div>
        {pro ? (
          historyRows.length === 0 ? (
            <div
              style={{
                marginTop: 8,
                fontFamily: "var(--sa-font-mono)",
                fontSize: 11,
                color: "var(--sa-fg-muted)",
              }}
            >
              No protection events yet. They appear here as weeks settle.
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0 0" }}>
              {historyRows.map((row, i) => (
                <li
                  key={`${row.label}-${String(i)}`}
                  data-week={row.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "60px 100px 1fr",
                    gap: 12,
                    padding: "4px 0",
                    borderBottom: "1px solid var(--sa-hairline)",
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 11.5,
                  }}
                >
                  <span style={{ color: "var(--sa-fg-secondary)" }}>{row.label}</span>
                  <span style={{ color: row.amount > 0 ? "var(--sa-accent-saffron)" : "var(--sa-fg-muted)" }}>
                    {row.amount > 0 ? `+${ghc(row.amount)}` : "—"}
                  </span>
                  <span style={{ color: "var(--sa-fg-muted)" }}>{row.note}</span>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.12em",
            }}
            data-gate="pro"
          >
            🔒 Unlock with Pro — see the per-week protection breakdown.
          </div>
        )}
      </div>
    </div>
  );
}
